import { t } from '../i18n';
// client/admin-client/src/components/GameList.jsx
// GameList.jsx
import React, { useMemo, useState } from 'react';
import axios from '../api/axios';
// [ADD][ANCHOR:INFO_EDIT:IMPORTS]
import InfoPresetBuilder, { defaultInfoDraft, buildPresetRaw, buildTimePayload, buildInfoTargetPayload, parsePresetRaw, formatTimeHuman, validateInfoTimerDraft, TARGET_LANGUAGE_OPTIONS, normalizeInfoTargetDevice, normalizeInfoTargetLanguage, normalizeInfoTargetCountry, } from './InfoPresetBuilder';
function getAuthHeaders() {
    const token = localStorage.getItem('token') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
}
function fmtNumber(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return String(v ?? '');
    return n.toLocaleString();
}
function formatPercent2(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return '0.00';
    return Math.min(100, Math.max(0, n)).toFixed(2);
}
function formatPercent4(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return '0.0000';
    return Math.min(100, Math.max(0, n)).toFixed(4);
}
function formatNonNegative4(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0)
        return '0.0000';
    return n.toFixed(4);
}
function isValidPercent2(v) {
    if (v === '' || v === null || v === undefined)
        return true;
    const raw = String(v).trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw))
        return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 100;
}
// [ADD][ANCHOR:GAMELIST:TABLE_MISSION_NUMBER_HELPERS]
function toPositiveNumber(v) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
}
function prettyJson(v) {
    try {
        return JSON.stringify(v, null, 2);
    }
    catch {
        return String(v);
    }
}
// [ADD][ANCHOR:ADMIN_UI:I18N_HELPERS]
// JSONB(i18n-map) 또는 string을 "표시용 문자열"로 변환
function normalizeLangTag(raw) {
    let s = String(raw || '').trim();
    if (!s)
        return 'en';
    s = s.replace(/_/g, '-').toLowerCase();
    if (s === 'kr')
        return 'ko';
    if (s.startsWith('ko'))
        return 'ko';
    return s;
}
function toI18nText(v, lang = 'ko') {
    if (v == null)
        return '';
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number')
        return String(v);
    if (typeof v === 'object' && !Array.isArray(v)) {
        const l = normalizeLangTag(lang);
        const base = l.split('-')[0];
        const candidates = [l];
        if (base && base !== l)
            candidates.push(base);
        if (l === 'ko')
            candidates.push('kr');
        candidates.push('en');
        for (const k of candidates) {
            const val = v?.[k];
            if (typeof val === 'string' && val.trim())
                return val.trim();
            if (typeof val === 'number')
                return String(val);
        }
        try {
            return JSON.stringify(v);
        }
        catch {
            return String(v);
        }
    }
    return String(v);
}
// 저장용: 기존 i18n-map이 있으면 유지하면서 ko만 업데이트
function mergeKoI18n(existing, koText) {
    const t = (koText ?? '').toString().trim();
    if (!t)
        return null;
    const base = (existing && typeof existing === 'object' && !Array.isArray(existing)) ? existing : {};
    return { ...base, ko: t };
}
function sanitizeInfoFamilyPart(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9:_-]/g, '-').replace(/-+/g, '-').slice(0, 96);
}
function hashInfoFamilyValue(value) {
    const s = String(value || '');
    let h = 2166136261;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(36);
}
function canonicalInfoAction(value) {
    const raw = String(value || '').trim();
    if (!raw)
        return '';
    const lower = raw.toLowerCase();
    if (/avatar[-_\s]*reward(?:[-_\s]*event)?|avatarrewardevent|avatar[-_\s]*event|events?[-_/\s]*avatar[-_\s]*reward/i.test(lower))
        return 'popup:avatar-reward';
    if (/game[-_\s]*guide|gameguide|guide[-_\s]*game|poker[-_\s]*guide|holdem[-_\s]*guide|how[-_\s]*to[-_\s]*(?:profit|play)|max(?:imum)?[-_\s]*profit/i.test(lower))
        return 'popup:game-guide';
    if (/lounge[-_\s]*vip[-_\s]*(?:x2|2x|double)|loungevipx2|lounge[-_\s]*point[-_\s]*(?:x2|double)|lounge[-_\s]*points?[-_\s]*(?:guide|earn)|vip[-_\s]*point[-_\s]*(?:guide|x2)|lounge[-_\s]*vip[-_\s]*guide/i.test(lower))
        return 'popup:lounge-vip-x2-guide';
    try {
        const u = new URL(raw, 'https://local.invalid');
        const semantic = [u.pathname, u.hash, u.searchParams.get('event'), u.searchParams.get('modal'), u.searchParams.get('action'), u.searchParams.get('guide'), u.searchParams.get('popup')].filter(Boolean).join(' ').toLowerCase();
        if (/avatar[-_\s]*reward|avatarrewardevent/i.test(semantic))
            return 'popup:avatar-reward';
        if (/game[-_\s]*guide|poker[-_\s]*guide|holdem[-_\s]*guide/i.test(semantic))
            return 'popup:game-guide';
        if (/lounge[-_\s]*vip|loungevipx2|vip[-_\s]*point/i.test(semantic))
            return 'popup:lounge-vip-x2-guide';
        return `url:${u.hostname === 'local.invalid' ? '' : u.hostname.toLowerCase()}${u.pathname.replace(/\/+$/, '').toLowerCase() || '/'}`;
    }
    catch {
        return `action:${lower.replace(/[\s_-]+/g, '-')}`;
    }
}
function deriveInfoFamilyKey(code, row, draft = {}) {
    const explicit = String(draft?.family_key || draft?.familyKey || '').trim();
    if (explicit)
        return explicit;
    const rowPreset = parsePresetRaw(row?.preset || '');
    if (rowPreset?.family_key)
        return rowPreset.family_key;
    const actionCandidates = [
        draft?.url, rowPreset?.url, row?.url, row?.link_url, row?.linkUrl, row?.target_url, row?.targetUrl, row?.href, row?.action, row?.action_type, row?.event_key, row?.guide_key, row?.modal_key, row?.popup_key,
    ];
    const canonical = actionCandidates.map(canonicalInfoAction).find(Boolean);
    const codePart = sanitizeInfoFamilyPart(code || row?.display_code || 'display');
    if (canonical)
        return `ibf:${codePart}:a:${hashInfoFamilyValue(canonical)}`;
    if (row?.id != null)
        return `ibf:${codePart}:row:${row.id}`;
    return `ibf:${codePart}:new:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}
// [ADD][ANCHOR:ADMIN_UI:MISSION_PRIZE_CALC_HELPER]
function round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}
// [PATCH][ANCHOR:ADMIN_UI:MISSION_PRIZE_CALC_HELPER_TO_ADDON]
// ✅ "추가 지급" 방식:
// - playerPool = totalBet * playerRate (기본 1%)
// - agentPool  = playerPool * agentShare (예: 0.5 => 총 0.5%)
// - 각 rank의 agent는 player rank * agentShare (플레이어 상금에 "추가" 지급)
// - Total 컬럼은 player+agent (자동 모드는 기존 1.5% 흐름, 수동 모드는 입력값 합계)
function calcMissionPrize(totalBet, playerRate = 0.01, agentShare = 0.5, payoutMode = 'auto', manualPayouts = []) {
    const tb = Number(totalBet);
    if (!Number.isFinite(tb) || tb <= 0)
        return null;
    const mode = String(payoutMode || 'auto').toLowerCase() === 'manual' ? 'manual' : 'auto';
    if (mode === 'manual') {
        const lines = (Array.isArray(manualPayouts) ? manualPayouts : [])
            .map((row) => ({
            rank: Math.trunc(Number(row?.rank)),
            player: round2(Number(row?.player ?? 0)),
            agent: round2(Number(row?.agent ?? 0)),
        }))
            .filter((row) => Number.isInteger(row.rank) && row.rank > 0 && row.player >= 0 && row.agent >= 0)
            .sort((a, b) => a.rank - b.rank)
            .map((row) => ({ ...row, total: round2(row.player + row.agent) }));
        if (!lines.length)
            return null;
        const playerPool = round2(lines.reduce((sum, row) => sum + row.player, 0));
        const agentPool = round2(lines.reduce((sum, row) => sum + row.agent, 0));
        return {
            total_bet: tb,
            payout_mode: 'manual',
            player_rate: round2(playerPool / tb),
            agent_share: 0,
            agent_rate: round2(agentPool / tb),
            player_pool: playerPool,
            agent_pool: agentPool,
            total_pool: round2(playerPool + agentPool),
            player_remainder: 0,
            agent_remainder: 0,
            lines,
        };
    }
    const pr = Number.isFinite(Number(playerRate)) ? Number(playerRate) : 0.01;
    const share = Math.max(0, Math.min(1, Number(agentShare ?? 0.5)));
    const agentRate = round2(pr * share);
    const playerPool = round2(tb * pr);
    const p1 = round2(playerPool / 2);
    const p2 = round2(playerPool / 4);
    const p3 = round2(playerPool / 8);
    const p4 = round2(playerPool / 16);
    const p5 = round2(playerPool / 32);
    const playerSum = round2(p1 + p2 + p3 + p4 + p5);
    const playerRemainder = round2(playerPool - playerSum);
    const agentPool = round2(playerPool * share);
    const rows = [p1, p2, p3, p4, p5];
    const lines = rows.map((player, index) => ({
        rank: index + 1,
        player,
        agent: round2(player * share),
        total: round2(player + round2(player * share)),
    }));
    const agentSum = round2(lines.reduce((sum, row) => sum + row.agent, 0));
    return {
        total_bet: tb,
        payout_mode: 'auto',
        player_rate: pr,
        agent_share: share,
        agent_rate: agentRate,
        player_pool: playerPool,
        agent_pool: agentPool,
        total_pool: round2(playerPool + agentPool),
        player_remainder: playerRemainder,
        agent_remainder: round2(agentPool - agentSum),
        lines,
    };
}
// [PATCH][ANCHOR:INFO_EDIT:PROPS]
export default function GameList({ tableGroups = [], onNeedRefresh, displayMetaByCode = {}, displaySetsByCode = {}, }) {
    const [busyCode, setBusyCode] = useState('');
    const [loungeEditByCode, setLoungeEditByCode] = useState({});
    const [expanded, setExpanded] = useState(() => ({}));
    const [missionByCode, setMissionByCode] = useState(() => ({}));
    const [missionLoading, setMissionLoading] = useState(() => ({}));
    // [FIX][ANCHOR:GAMELIST:MISSION_BUSY_SCOPE_ISOLATION]
    // 그룹/테이블 미션 조작은 서로 독립적으로 잠금 처리한다.
    const [groupMissionBusyCode, setGroupMissionBusyCode] = useState('');
    const [tableMissionBusyCode, setTableMissionBusyCode] = useState('');
    // [PATCH][ANCHOR:GAMELIST:TABLE_MISSION_STATE]
    // ✅ 테이블 미션은 "display_code(테이블 그룹)" 단위로 기준(cfg/run)을 관리하고
    // 백엔드가 그룹 내 각 테이블에 자동 적용/생성한다. (table_code 입력 X)
    const [tableMissionByCode, setTableMissionByCode] = useState(() => ({})); // { [display_code]: { config, active, ... } }
    const [tableMissionLoading, setTableMissionLoading] = useState(() => ({})); // { [display_code]: boolean }
    // [ADD][ANCHOR:INFO_EDIT:STATE_AND_HELPERS]
    const [infoEdit, setInfoEdit] = useState(null); // { mode: 'create' | 'edit', code, row }
    const [infoDraft, setInfoDraft] = useState(defaultInfoDraft());
    const [infoSaving, setInfoSaving] = useState(false);
    function normalizeTime(t) {
        if (!t)
            return null;
        if (typeof t === 'object')
            return t;
        if (typeof t === 'string') {
            try {
                return JSON.parse(t);
            }
            catch {
                return null;
            }
        }
        return null;
    }
    // ISO(UTC) -> datetime-local 포맷(로컬)
    function isoToLocalInput(iso) {
        if (!iso)
            return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return '';
        d.setSeconds(0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    function closeInfoDialog() {
        setInfoEdit(null);
        setInfoDraft(defaultInfoDraft());
    }
    function openInfoCreate(code) {
        const base = defaultInfoDraft();
        setInfoDraft({ ...base, family_key: deriveInfoFamilyKey(code, null, base) });
        setInfoEdit({ mode: 'create', code, row: null });
    }
    function openInfoEdit(code, row) {
        const base = defaultInfoDraft();
        const t = normalizeTime(row?.time) || { mode: 'infinite', refresh_days: 30 };
        const preset = parsePresetRaw(row?.preset || '');
        setInfoDraft({
            ...base,
            ...preset,
            family_key: deriveInfoFamilyKey(code, row, preset),
            highlight_point: toI18nText(row?.highlight_point, 'ko') || '',
            highlight_message: toI18nText(row?.highlight_message, 'ko') || '',
            status: row?.status || 'active',
            device: row?.device || 'all',
            language: row?.language || 'all',
            country: row?.country || 'ALL',
            priority: row?.priority ?? 0,
            time_mode: t?.mode === 'range' ? 'range' : 'infinite',
            start_local: t?.start_utc ? isoToLocalInput(t.start_utc) : '',
            end_local: t?.end_utc ? isoToLocalInput(t.end_utc) : '',
            refresh_days: Number(t?.refresh_days || 30),
        });
        setInfoEdit({ mode: 'edit', code, row });
    }
    async function saveInfoEdit() {
        if (!infoEdit?.code)
            return;
        if (infoEdit.mode !== 'create' && !infoEdit?.row?.id)
            return;
        const timerValidation = validateInfoTimerDraft(infoDraft);
        if (!timerValidation.ok) {
            alert(timerValidation.message || t("Due Date\uB97C \uC785\uB825\uD574\uC57C \uD569\uB2C8\uB2E4."));
            return;
        }
        const time = buildTimePayload(infoDraft);
        if (!time) {
            alert(t("\uC2DC\uAC04 \uC124\uC815\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
            return;
        }
        setInfoSaving(true);
        try {
            const normalizedDraft = {
                ...infoDraft,
                family_key: deriveInfoFamilyKey(infoEdit.code, infoEdit?.row, infoDraft),
            };
            const payload = {
                status: normalizedDraft.status || 'active',
                time,
                preset: buildPresetRaw(normalizedDraft),
                ...buildInfoTargetPayload(normalizedDraft),
            };
            if (infoEdit.mode === 'create') {
                payload.highlight_point = infoDraft.highlight_point || null;
                payload.highlight_message = infoDraft.highlight_message || null;
                await axios.post(`/admin/table/group/${infoEdit.code}/info`, payload, { headers: getAuthHeaders() });
            }
            else {
                await axios.patch(`/admin/table/group/${infoEdit.code}/display-sets/${infoEdit.row.id}`, {
                    ...payload,
                    // ✅ JSONB 컬럼 저장: 기존 맵 유지 + ko만 업데이트
                    highlight_point: mergeKoI18n(infoEdit?.row?.highlight_point, infoDraft.highlight_point),
                    highlight_message: mergeKoI18n(infoEdit?.row?.highlight_message, infoDraft.highlight_message),
                }, { headers: getAuthHeaders() });
            }
            closeInfoDialog();
            await onNeedRefresh?.();
        }
        catch (e) {
            alert(e?.response?.data?.message || (infoEdit.mode === 'create' ? t("\uC778\uD3EC\uCE74\uB4DC \uCD94\uAC00 \uC2E4\uD328") : t("\uC778\uD3EC\uBC84\uD2BC \uD504\uB9AC\uC14B \uC218\uC815 \uC2E4\uD328")));
        }
        finally {
            setInfoSaving(false);
        }
    }
    // [ADD][ANCHOR:GAMELIST:DELETE_INFO_CARD]
    async function deleteInfoCard(code, row) {
        const displayCode = String(code || '').trim();
        const id = Number(row?.id);
        if (!displayCode || !Number.isFinite(id) || id <= 0)
            return;
        const title = toI18nText(row?.highlight_point, 'ko') || t('인포카드');
        const ok = window.confirm(`${title}\n\n${t('이 인포카드를 삭제하시겠습니까? 같은 카드의 데스크톱/모바일·언어별 변형도 함께 삭제됩니다. 테이블 그룹과 게임 테이블은 삭제되지 않습니다.')}`);
        if (!ok)
            return;
        setInfoSaving(true);
        try {
            await axios.delete(`/admin/table/group/${displayCode}/info/${id}`, { headers: getAuthHeaders() });
            if (infoEdit?.code === displayCode) {
                closeInfoDialog();
            }
            await onNeedRefresh?.();
        }
        catch (e) {
            alert(e?.response?.data?.message || t('인포카드 삭제 실패'));
        }
        finally {
            setInfoSaving(false);
        }
    }
    // [ADD][ANCHOR:GAMELIST:INFO_TARGET_VARIANT_MATRIX]
    const infoVariantDevices = [
        { value: 'desktop', label: t('데스크톱') },
        { value: 'mobile', label: t('모바일') },
    ];
    const infoVariantLanguages = TARGET_LANGUAGE_OPTIONS.filter((opt) => opt.value !== 'all');
    function getInfoRowsForCurrentEditCode() {
        const code = infoEdit?.code;
        if (!code)
            return [];
        const items = Array.isArray(displaySetsByCode?.[code]) ? displaySetsByCode[code] : [];
        return items.filter((x) => x?.type === 'information_button');
    }
    function findExactInfoVariant(device, language) {
        const nd = normalizeInfoTargetDevice(device);
        const nl = normalizeInfoTargetLanguage(language);
        const currentFamily = deriveInfoFamilyKey(infoEdit?.code, infoEdit?.row, infoDraft);
        return getInfoRowsForCurrentEditCode().find((row) => {
            const parsed = parsePresetRaw(row?.preset || '');
            const rowFamily = deriveInfoFamilyKey(infoEdit?.code, row, parsed);
            return rowFamily === currentFamily &&
                normalizeInfoTargetDevice(row?.device) === nd &&
                normalizeInfoTargetLanguage(row?.language) === nl &&
                normalizeInfoTargetCountry(row?.country) === 'ALL';
        });
    }
    function getInfoVariantBgLabel(row) {
        if (!row)
            return t('미생성');
        const parsed = parsePresetRaw(row?.preset || '');
        if (parsed.design_enabled && parsed.design_image_url)
            return t('디자인 이미지');
        if (parsed.design_enabled)
            return t('디자인');
        if (parsed.bg_mode === 'image' && parsed.bg_image_url)
            return t('이미지');
        if (parsed.bg_mode === 'color')
            return parsed.bg_color || t('색상');
        return t('배경 없음');
    }
    async function createInfoTargetVariantFromCurrent(device, language) {
        if (!infoEdit?.code)
            return;
        const existed = findExactInfoVariant(device, language);
        if (existed) {
            openInfoEdit(infoEdit.code, existed);
            return;
        }
        const timerValidation = validateInfoTimerDraft(infoDraft);
        if (!timerValidation.ok) {
            alert(timerValidation.message || t("Due Date를 입력해야 합니다."));
            return;
        }
        const time = buildTimePayload(infoDraft);
        if (!time) {
            alert(t("시간 설정이 올바르지 않습니다."));
            return;
        }
        const nextDraft = {
            ...infoDraft,
            family_key: deriveInfoFamilyKey(infoEdit.code, infoEdit?.row, infoDraft),
            device: normalizeInfoTargetDevice(device),
            language: normalizeInfoTargetLanguage(language),
            country: 'ALL',
        };
        setInfoSaving(true);
        try {
            const res = await axios.post(`/admin/table/group/${infoEdit.code}/info`, {
                highlight_point: mergeKoI18n(null, nextDraft.highlight_point),
                highlight_message: mergeKoI18n(null, nextDraft.highlight_message),
                status: nextDraft.status || 'active',
                time,
                preset: buildPresetRaw(nextDraft),
                ...buildInfoTargetPayload(nextDraft),
            }, { headers: getAuthHeaders() });
            const created = res?.data?.item || res?.data?.displaySet?.information_buttons?.[0] || null;
            await onNeedRefresh?.();
            if (created)
                openInfoEdit(infoEdit.code, created);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("타겟별 인포 버튼 생성 실패"));
        }
        finally {
            setInfoSaving(false);
        }
    }
    function renderInfoTargetVariantMatrix() {
        if (!infoEdit?.code)
            return null;
        const currentDevice = normalizeInfoTargetDevice(infoDraft.device);
        const currentLanguage = normalizeInfoTargetLanguage(infoDraft.language);
        return (<div className="mb-4 border rounded p-3 bg-amber-50/60">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold">{t("디바이스/언어별 배경 빠른 관리")}</div>
                <div className="text-xs text-gray-600 mt-1">{t("각 칸은 별도 인포 버튼 항목입니다. 없으면 현재 설정을 복제해서 생성하고, 있으면 해당 항목을 열어 배경 이미지를 따로 바꿀 수 있습니다.")}</div>
              </div>
              <div className="text-xs text-gray-600">
                {t("현재 편집:")} {currentDevice} / {currentLanguage}
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {infoVariantDevices.map((dev) => (<div key={dev.value} className="border rounded bg-white p-2">
                  <div className="font-medium mb-2">{dev.label}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    {infoVariantLanguages.map((lang) => {
                        const row = findExactInfoVariant(dev.value, lang.value);
                        const isCurrent = currentDevice === dev.value && currentLanguage === lang.value;
                        return (<button key={`${dev.value}-${lang.value}`} type="button" disabled={infoSaving} onClick={() => row
                                ? openInfoEdit(infoEdit.code, row)
                                : createInfoTargetVariantFromCurrent(dev.value, lang.value)} className={[
                                "text-left rounded border px-2 py-2 hover:bg-gray-50 disabled:opacity-60",
                                row ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white",
                                isCurrent ? "ring-2 ring-gray-900" : "",
                            ].join(' ')} title={row ? t("기존 타겟 항목 열기") : t("현재 설정으로 타겟 항목 생성")}>
                            <div className="font-semibold text-sm">{lang.label}</div>
                            <div className="text-xs text-gray-600 mt-1">{row ? t("기존") : t("생성")}</div>
                            <div className="text-xs text-gray-500 truncate">{getInfoVariantBgLabel(row)}</div>
                          </button>);
                    })}
                  </div>
                </div>))}
            </div>
          </div>);
    }

    const groups = useMemo(() => tableGroups || [], [tableGroups]);
    function getLoungeDraft(group) {
        const code = group?.display_code;
        return loungeEditByCode?.[code] || {
            rake_percent: formatPercent4(group?.rake_percent ?? 0),
            rake_cap_bb: formatNonNegative4(group?.rake_cap_bb ?? 0),
            event_fee_percent: formatPercent4(group?.event_fee_percent ?? 0),
            lounge_point_percent: formatPercent2(group?.lounge_point_percent ?? 0),
            apply_to_active_tables: false,
        };
    }
    function updateLoungeDraft(display_code, patch) {
        const group = (tableGroups || []).find((g) => g.display_code === display_code) || {};
        setLoungeEditByCode((prev) => ({
            ...(prev || {}),
            [display_code]: {
                rake_percent: formatPercent4(group?.rake_percent ?? 0),
                rake_cap_bb: formatNonNegative4(group?.rake_cap_bb ?? 0),
                event_fee_percent: formatPercent4(group?.event_fee_percent ?? 0),
                lounge_point_percent: formatPercent2(group?.lounge_point_percent ?? 0),
                apply_to_active_tables: false,
                ...(prev?.[display_code] || {}),
                ...patch,
            },
        }));
    }
    async function saveLoungePointPercent(group) {
        const display_code = group?.display_code;
        if (!display_code)
            return;
        const draft = getLoungeDraft(group);
        if (!Number.isFinite(Number(draft.rake_percent)) || Number(draft.rake_percent) < 0 || Number(draft.rake_percent) > 100) {
            alert(t('Rake %는 0~100 사이 숫자여야 합니다.'));
            return;
        }
        if (!Number.isFinite(Number(draft.rake_cap_bb)) || Number(draft.rake_cap_bb) < 0) {
            alert(t('Rake Cap BB는 0 이상의 숫자여야 합니다.'));
            return;
        }
        if (!Number.isFinite(Number(draft.event_fee_percent)) || Number(draft.event_fee_percent) < 0 || Number(draft.event_fee_percent) > 100) {
            alert(t('Event Fee %는 0~100 사이 숫자여야 합니다.'));
            return;
        }
        if (!isValidPercent2(draft.lounge_point_percent)) {
            alert(t("\uB77C\uC6B4\uC9C0 \uD3EC\uC778\uD2B8 %\uB294 0~100 \uC0AC\uC774 \uC22B\uC790\uC774\uBA70 \uC18C\uC218\uC810\uC740 \uCD5C\uB300 2\uC790\uB9AC\uAE4C\uC9C0 \uAC00\uB2A5\uD569\uB2C8\uB2E4."));
            return;
        }
        setBusyCode(display_code);
        try {
            await axios.patch(`/admin/table/group/${display_code}`, {
                rake_percent: String(draft.rake_percent || '0'),
                rake_cap_bb: String(draft.rake_cap_bb || '0'),
                event_fee_percent: String(draft.event_fee_percent || '0'),
                lounge_point_percent: draft.lounge_point_percent === '' ? '0.00' : String(draft.lounge_point_percent),
                apply_to_active_tables: Boolean(draft.apply_to_active_tables),
            }, { headers: getAuthHeaders() });
            setLoungeEditByCode((prev) => {
                const next = { ...(prev || {}) };
                delete next[display_code];
                return next;
            });
            await onNeedRefresh?.();
        }
        catch (e) {
            alert(e?.response?.data?.message || t("테이블 그룹 정산 정책 저장 실패"));
        }
        finally {
            setBusyCode('');
        }
    }
    async function deleteGroup(display_code) {
        if (!window.confirm(t('테이블 그룹 삭제?\n({displayCode})', { displayCode: display_code })))
            return;
        setBusyCode(display_code);
        try {
            // [PATCH][ANCHOR:ADMIN_UI_DELETE_GROUP_WITH_PURGE_QUERY]
            const { data } = await axios.delete(`/admin/table/group/${display_code}?force=1&delete_runs=1`, { headers: getAuthHeaders() });
            // [ADD][ANCHOR:ADMIN_UI_DELETE_GROUP_WARN_IF_PURGE_FAILED]
            if (data?.purge && data.purge.ok === false) {
                alert(t('그룹은 삭제됐지만 Redis 정리가 실패했습니다. (code={code})', { code: data.purge.code || 'UNKNOWN' }));
            }
            await onNeedRefresh?.();
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uC0AD\uC81C \uC2E4\uD328"));
        }
        finally {
            setBusyCode('');
        }
    }
    async function loadMissionStatus(display_code) {
        setMissionLoading((p) => ({ ...p, [display_code]: true }));
        try {
            const res = await axios.get(`/admin/mission/group/${display_code}`, {
                headers: getAuthHeaders(),
            });
            if (res?.data?.scope && res.data.scope !== 'group') {
                throw new Error(`MISSION_SCOPE_MISMATCH:${res.data.scope}`);
            }
            setMissionByCode((p) => ({ ...p, [display_code]: res.data }));
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uBBF8\uC158 \uC0C1\uD0DC \uC870\uD68C \uC2E4\uD328"));
        }
        finally {
            setMissionLoading((p) => ({ ...p, [display_code]: false }));
        }
    }
    async function setMissionConfig(display_code) {
        // [PATCH][ANCHOR:ADMIN_UI:MISSION_CONFIG_PROMPT_READ_CONFIG]
        const currentTarget = missionByCode?.[display_code]?.config?.target_total_bet ??
            missionByCode?.[display_code]?.cfg?.target_total_bet ??
            '';
        const input = window.prompt(t("\uBBF8\uC158 \uCD1D \uBCA0\uD305\uAE08(target_total_bet)\uC744 \uC785\uB825"), String(currentTarget));
        if (input === null)
            return;
        const n = Number(input);
        if (!Number.isFinite(n) || n <= 0) {
            alert(t("0\uBCF4\uB2E4 \uD070 \uC22B\uC790\uB9CC \uAC00\uB2A5"));
            return;
        }
        // [ADD][ANCHOR:ADMIN_UI:MISSION_CONFIG_AGENT_SHARE_PROMPT]
        const currentShare = missionByCode?.[display_code]?.config?.agent_share ??
            missionByCode?.[display_code]?.cfg?.agent_share ??
            0.5;
        const shareInput = window.prompt(t("\uC5D0\uC774\uC804\uD2B8 \uBD84\uBC30 \uBE44\uC728(agent_share) \uC785\uB825 (\uC608: 0.2 = 20%)"), String(currentShare));
        if (shareInput === null)
            return;
        const agent_share = Number(shareInput);
        if (!Number.isFinite(agent_share) || agent_share < 0 || agent_share > 1) {
            alert(t("agent_share\uB294 0~1 \uC0AC\uC774 \uC22B\uC790\uB9CC \uAC00\uB2A5 (\uC608: 0.2)"));
            return;
        }
        setGroupMissionBusyCode(display_code);
        try {
            await axios.put('/admin/mission/config', { display_code, target_total_bet: n, agent_share }, { headers: getAuthHeaders() });
            await loadMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uBBF8\uC158 cfg \uC800\uC7A5 \uC2E4\uD328"));
        }
        finally {
            setGroupMissionBusyCode('');
        }
    }
    async function startMissionRun(display_code) {
        // cfg가 있으면 그 값 우선, 없으면 prompt
        // [PATCH][ANCHOR:ADMIN_UI:MISSION_RUN_TARGET_READ_CONFIG]
        let target = missionByCode?.[display_code]?.config?.target_total_bet ??
            missionByCode?.[display_code]?.cfg?.target_total_bet;
        if (!target) {
            const input = window.prompt(t("RUN \uC0DD\uC131: \uBBF8\uC158 \uCD1D \uBCA0\uD305\uAE08(target_total_bet) \uC785\uB825"), '');
            if (input === null)
                return;
            const n = Number(input);
            if (!Number.isFinite(n) || n <= 0) {
                alert(t("0\uBCF4\uB2E4 \uD070 \uC22B\uC790\uB9CC \uAC00\uB2A5"));
                return;
            }
            target = n;
        }
        setGroupMissionBusyCode(display_code);
        try {
            await axios.post('/admin/mission/run', { display_code, target_total_bet: Number(target) }, { headers: getAuthHeaders() });
            await loadMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uBBF8\uC158 RUN \uC0DD\uC131 \uC2E4\uD328"));
        }
        finally {
            setGroupMissionBusyCode('');
        }
    }
    // [ADD][ANCHOR:GAMELIST:GROUP_MISSION_STOP]
    // ✅ 그룹 미션 중지(= active run 종료)
    // run_id 포인터가 없어도 display_code 기준 복구/정리 endpoint로 중지 가능
    async function stopMissionRun(display_code) {
        const ms = missionByCode?.[display_code];
        const run = ms?.active || ms?.active_run || null;
        const run_id = run?.run_id || null;
        const confirmText = run_id
            ? t('그룹 미션 RUN 중지?\nrun_id={runId}', { runId: run_id })
            : t('그룹 미션 실행 상태를 정리/중지할까요?\n(display_code={displayCode})', { displayCode: display_code });
        if (!window.confirm(confirmText))
            return;
        setGroupMissionBusyCode(display_code);
        try {
            // [FIX][ANCHOR:GAMELIST:GROUP_MISSION_STOP_BY_DISPLAY]
            // active run_id가 유실된 비정상 상태에서도 display_code 기준으로 정리 가능.
            // 테이블 미션과는 별도 endpoint/key를 사용한다.
            await axios.delete(`/admin/mission/group/${display_code}/run`, { headers: getAuthHeaders() });
            await loadMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("그룹 미션 중지 실패"));
        }
        finally {
            setGroupMissionBusyCode('');
        }
    }
    // [ADD][ANCHOR:GAMELIST:TABLE_MISSION_GROUP_BASED_FNS]
    // ✅ 테이블 미션(테이블그룹 기준) : display_code 단위로 cfg/run을 관리
    // 백엔드가 그룹 내 각 테이블(1테이블=1미션)에 자동 적용한다. (table_code 입력 X)
    async function loadTableMissionStatus(display_code) {
        setTableMissionLoading((p) => ({ ...p, [display_code]: true }));
        try {
            // ✅ display_code 기준 상태 조회
            const res = await axios.get(`/admin/mission/table/group/${display_code}`, {
                headers: getAuthHeaders(),
            });
            if (res?.data?.scope && res.data.scope !== 'table') {
                throw new Error(`MISSION_SCOPE_MISMATCH:${res.data.scope}`);
            }
            setTableMissionByCode((p) => ({ ...(p || {}), [display_code]: res.data }));
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uD14C\uC774\uBE14 \uBBF8\uC158 \uC0C1\uD0DC \uC870\uD68C \uC2E4\uD328"));
        }
        finally {
            setTableMissionLoading((p) => ({ ...p, [display_code]: false }));
        }
    }
    async function setTableMissionConfig(display_code) {
        const tms = tableMissionByCode?.[display_code];
        const cfg = tms?.config || tms?.cfg || null;
        const currentTarget = cfg?.target_total_bet ?? '';
        const input = window.prompt(t("테이블 미션 총 베팅금(target_total_bet)을 입력"), String(currentTarget));
        if (input === null)
            return;
        const n = Number(input);
        if (!Number.isFinite(n) || n <= 0) {
            alert(t("0보다 큰 숫자만 가능"));
            return;
        }

        const currentMode = String(cfg?.payout_mode || (Array.isArray(cfg?.manual_payouts) && cfg.manual_payouts.length ? 'manual' : 'auto')).toLowerCase();
        const modeInput = window.prompt(t("상금 방식 입력: auto = 기존 자동계산 / manual = 순위별 수동설정"), currentMode);
        if (modeInput === null)
            return;
        const payout_mode = String(modeInput).trim().toLowerCase();
        if (!['auto', 'manual'].includes(payout_mode)) {
            alert(t("상금 방식은 auto 또는 manual 로 입력해 주세요."));
            return;
        }

        let agent_share = Number(cfg?.agent_share ?? 0.5);
        let manual_payouts = [];
        if (payout_mode === 'auto') {
            const shareInput = window.prompt(t("테이블 미션 에이전트 분배 비율(agent_share) 입력 (예: 0.2 = 20%)"), String(agent_share));
            if (shareInput === null)
                return;
            agent_share = Number(shareInput);
            if (!Number.isFinite(agent_share) || agent_share < 0 || agent_share > 1) {
                alert(t("agent_share는 0~1 사이 숫자만 가능 (예: 0.2)"));
                return;
            }
        }
        else {
            const existingRows = Array.isArray(cfg?.manual_payouts) ? cfg.manual_payouts : [];
            const countInput = window.prompt(t("수동 상금을 지급할 순위 수를 입력하세요. (예: 2 = 1등, 2등)"), String(existingRows.length || 2));
            if (countInput === null)
                return;
            const rankCount = Math.trunc(Number(countInput));
            if (!Number.isInteger(rankCount) || rankCount < 1 || rankCount > 50) {
                alert(t("순위 수는 1~50 사이 정수로 입력해 주세요."));
                return;
            }
            for (let rank = 1; rank <= rankCount; rank += 1) {
                const existing = existingRows.find((row) => Number(row?.rank) === rank) || {};
                const playerInput = window.prompt(t('{rank}등 플레이어 당첨 금액을 입력하세요.', { rank }), String(existing?.player ?? ''));
                if (playerInput === null)
                    return;
                const player = Number(playerInput);
                if (!Number.isFinite(player) || player <= 0) {
                    alert(t("플레이어 당첨 금액은 0보다 커야 합니다."));
                    return;
                }
                const agentInput = window.prompt(t('{rank}등 에이전트 몫을 입력하세요. 0이면 지급 없음.', { rank }), String(existing?.agent ?? 0));
                if (agentInput === null)
                    return;
                const agent = Number(agentInput);
                if (!Number.isFinite(agent) || agent < 0) {
                    alert(t("에이전트 몫은 0 이상의 숫자여야 합니다."));
                    return;
                }
                manual_payouts.push({ rank, player: round2(player), agent: round2(agent) });
            }
            agent_share = 0;
        }

        setTableMissionBusyCode(display_code);
        try {
            await axios.put('/admin/mission/table/config', {
                display_code,
                target_total_bet: n,
                payout_mode,
                agent_share,
                manual_payouts,
            }, { headers: getAuthHeaders() });
            await loadTableMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("테이블 미션 cfg 저장 실패"));
        }
        finally {
            setTableMissionBusyCode('');
        }
    }
    async function startTableMissionRun(display_code) {
        // cfg가 있으면 우선, 없으면 prompt로 입력 (그룹 미션과 동일 UX)
        const tms = tableMissionByCode?.[display_code];
        const cfg = tms?.config || tms?.cfg || null;
        // [PATCH][ANCHOR:GAMELIST:TABLE_MISSION_START_SHOULD_NOT_PROMPT_WHEN_ACTIVE]
        // ✅ 이미 active RUN이면: 시작 팝업(타겟 입력) 띄우지 말고 재시작 여부만 확인
        const active = tms?.active || tms?.active_run || null;
        const activeRunId = active?.run_id || null;
        let force = false;
        if (activeRunId) {
            const ok = window.confirm(t('이미 테이블 미션 RUN이 실행 중입니다.\n(run_id={runId})\n재시작(기존 RUN 삭제 후 새로 생성)할까요?', { runId: activeRunId }));
            if (!ok)
                return;
            force = true;
            // 재시작이면 먼저 중지(삭제) 하고 다시 생성
            try {
                await axios.delete(`/admin/mission/table/run/group/${display_code}`, {
                    headers: getAuthHeaders(),
                });
            }
            catch (_) {
                // 중지 실패해도 아래에서 force로 덮어쓰기 시도할 수 있게 그냥 진행
            }
        }
        let target = cfg?.target_total_bet ?? null;
        if (!target) {
            const input = window.prompt(t("RUN \uC0DD\uC131: \uD14C\uC774\uBE14 \uBBF8\uC158 \uCD1D \uBCA0\uD305\uAE08(target_total_bet) \uC785\uB825"), '');
            if (input === null)
                return;
            const n = Number(input);
            if (!Number.isFinite(n) || n <= 0) {
                alert(t("0\uBCF4\uB2E4 \uD070 \uC22B\uC790\uB9CC \uAC00\uB2A5"));
                return;
            }
            target = n;
        }
        setTableMissionBusyCode(display_code);
        try {
            // ✅ display_code 기준 RUN 생성 (백엔드가 그룹 내 각 테이블에 자동 적용)
            const agent_share = Number(cfg?.agent_share ?? 0.5);
            await axios.post('/admin/mission/table/run', {
                display_code,
                target_total_bet: Number(target),
                agent_share: Number.isFinite(agent_share) ? agent_share : 0.5,
                payout_mode: cfg?.payout_mode || 'auto',
                manual_payouts: Array.isArray(cfg?.manual_payouts) ? cfg.manual_payouts : [],
                force,
            }, { headers: getAuthHeaders() });
            await loadTableMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uD14C\uC774\uBE14 \uBBF8\uC158 RUN \uC0DD\uC131 \uC2E4\uD328"));
        }
        finally {
            setTableMissionBusyCode('');
        }
    }
    async function stopTableMissionRun(display_code) {
        if (!window.confirm(t('테이블 미션 RUN 중지(그룹 전체 적용)?\n(display_code={displayCode})', { displayCode: display_code })))
            return;
        setTableMissionBusyCode(display_code);
        try {
            // ✅ display_code 기준 RUN 종료
            await axios.delete(`/admin/mission/table/run/group/${display_code}`, {
                headers: getAuthHeaders(),
            });
            await loadTableMissionStatus(display_code);
        }
        catch (e) {
            alert(e?.response?.data?.message || t("\uD14C\uC774\uBE14 \uBBF8\uC158 RUN \uC911\uC9C0 \uC2E4\uD328"));
        }
        finally {
            setTableMissionBusyCode('');
        }
    }
    function toggleExpand(display_code) {
        setExpanded((p) => {
            const next = !p[display_code];
            return { ...p, [display_code]: next };
        });
        // 펼칠 때만 로드
        if (!expanded[display_code]) {
            loadMissionStatus(display_code);
            // [PATCH][ANCHOR:GAMELIST:TABLE_MISSION_AUTOLOAD_ON_EXPAND]
            loadTableMissionStatus(display_code);
        }
    }
    if (!groups.length) {
        return <div className="text-gray-600">{t("\uD14C\uC774\uBE14 \uADF8\uB8F9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>;
    }
    return (<div className="space-y-3">
      {groups.map((g) => {
            const code = g.display_code;
            const ms = missionByCode?.[code];
            // [PATCH][ANCHOR:ADMIN_UI:MISSION_STATUS_KEYMAP_FIX]
            // 백엔드 응답: { config, active } 형태
            const cfg = ms?.config || ms?.cfg || null;
            const run = ms?.active || ms?.active_run || null;
            // [ADD][ANCHOR:ADMIN_UI:TABLE_MISSION_STATUS_KEYMAP]
            const tms = tableMissionByCode?.[code];
            const tcfg = tms?.config || tms?.cfg || null;
            const trun = tms?.active || tms?.active_run || null;
            return (<div key={code} className="border rounded p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="font-semibold">
                  {toI18nText(g.name, 'ko')} <span className="text-xs text-gray-500">({code})</span>
                </div>
                <div className="text-sm text-gray-700">
                  SB/BB: {g.small_blind}/{g.big_blind} · Game: {g.default_game} · MaxPlayers:{' '}
                  {g.max_players} · MaxCount: {g.max_count} · Rake: {g.rake_percent}% · Cap: {formatNonNegative4(g.rake_cap_bb)}bb · Event Fee: {formatPercent4(g.event_fee_percent)}% · Lounge Point: {formatPercent2(g.lounge_point_percent)}%
                </div>
                <div className="text-xs text-gray-500">
                  Lock: {g.lock} {g.lock === 'LOCK' ? t("(password \uC788\uC74C)") : ''}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={() => toggleExpand(code)} className="px-3 py-1.5 rounded border hover:bg-gray-50">
                  {expanded[code] ? t("\uB2EB\uAE30") : t("\uC0C1\uC138")}
                </button>

                <button onClick={() => setMissionConfig(code)} disabled={groupMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uADF8\uB8F9 \uBBF8\uC158 \uC124\uC815")}</button>

                <button onClick={() => startMissionRun(code)} disabled={groupMissionBusyCode === code} className="px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60">{t("\uADF8\uB8F9 \uBBF8\uC158 \uC2DC\uC791")}</button>

                <button onClick={() => deleteGroup(code)} disabled={busyCode === code} className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-500 disabled:opacity-60">{t("\uC0AD\uC81C")}</button>
              </div>
            </div>

            {expanded[code] ? (<div className="mt-3 p-3 rounded bg-gray-50 border text-sm">
                <div className="mb-3 p-3 rounded border bg-white">
                  <div className="font-semibold mb-2">{t("테이블 그룹 정산 정책")}</div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600">Rake %</span>
                      <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.0001" value={getLoungeDraft(g).rake_percent} onChange={(e) => updateLoungeDraft(code, { rake_percent: e.target.value })} placeholder="9"/>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600">Rake Cap (BB)</span>
                      <input className="border rounded px-3 py-2" type="number" min="0" step="0.0001" value={getLoungeDraft(g).rake_cap_bb} onChange={(e) => updateLoungeDraft(code, { rake_cap_bb: e.target.value })} placeholder="15"/>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600">Event Fee %</span>
                      <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.0001" value={getLoungeDraft(g).event_fee_percent} onChange={(e) => updateLoungeDraft(code, { event_fee_percent: e.target.value })} placeholder="1"/>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600">{t("라운지 포인트 %")}</span>
                      <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.01" value={getLoungeDraft(g).lounge_point_percent} onChange={(e) => updateLoungeDraft(code, { lounge_point_percent: e.target.value })} placeholder="0.25"/>
                    </label>
                    <button type="button" onClick={() => saveLoungePointPercent(g)} disabled={busyCode === code} className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60">{t("저장")}</button>
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={Boolean(getLoungeDraft(g).apply_to_active_tables)} onChange={(e) => updateLoungeDraft(code, { apply_to_active_tables: e.target.checked })}/>{t("현재 활성 테이블에도 즉시 적용")}
                  </label>
                  <div className="text-xs text-gray-500 mt-2">{t("체크하지 않으면 앞으로 새로 생성되는 테이블에만 적용됩니다.")}</div>
                </div>
               {/* [ADD][ANCHOR:INFO_EDIT:RENDER_IN_EXPANDED] */}
                {(() => {
                        const sets = Array.isArray(displaySetsByCode?.[code]) ? displaySetsByCode[code] : [];
                        const infoRows = sets.filter((x) => x?.type === 'information_button');
                        const groupRow = sets.find((x) => x?.type === 'table_group') ||
                            displayMetaByCode?.[code] ||
                            null;
                        return (<div className="mb-3 p-3 rounded border bg-white">
                      <div className="font-semibold mb-2">DisplaySet</div>

                      <div className="text-xs text-gray-600">
                        group.time: {formatTimeHuman(normalizeTime(groupRow?.time))}
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="font-medium">
                          Information Buttons ({infoRows.length})
                        </div>
                        <button type="button" onClick={() => openInfoCreate(code)} disabled={infoSaving} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60">{t("+ \uC778\uD3EC\uCE74\uB4DC \uCD94\uAC00")}</button>
                      </div>

                      {infoRows.length === 0 ? (<div className="text-xs text-gray-500 mt-1">{t("\uC778\uD3EC\uBC84\uD2BC \uC5C6\uC74C")}</div>) : (<div className="mt-2 space-y-2">
                          {infoRows.map((r) => (<div key={r.id} className="p-2 rounded border bg-gray-50 flex items-start justify-between gap-3">
                              <div>
                                <div className="font-medium">{toI18nText(r.highlight_point, 'ko') || '(no title)'}</div>
                               <div className="text-xs text-gray-700">{toI18nText(r.highlight_message, 'ko') || ''}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  status: {r.status || '-'} · time: {formatTimeHuman(normalizeTime(r.time))}
                                </div>
                                <div className="text-xs text-blue-700 mt-0.5">
                                  target: {r.device || 'all'} / {r.language || 'all'} / {r.country || 'ALL'} · priority: {r.priority ?? 0}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <button type="button" onClick={() => openInfoEdit(code, r)} disabled={infoSaving} className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-60">{t("\uD504\uB9AC\uC14B \uC218\uC815")}</button>
                                <button type="button" onClick={() => deleteInfoCard(code, r)} disabled={infoSaving} className="px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60">{t("\uC0AD\uC81C")}</button>
                              </div>
                            </div>))}
                        </div>)}
                    </div>);
                    })()}
              {/* [ADD][ANCHOR:GAMELIST:MISSION_TWO_PANELS] */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* ===== Group Mission Panel ===== */}
                  <div className="p-3 rounded border bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold">{t("\uADF8\uB8F9 \uBBF8\uC158")}</div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button type="button" onClick={() => loadMissionStatus(code)} disabled={groupMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uC870\uD68C")}</button>
                        <button type="button" onClick={() => setMissionConfig(code)} disabled={groupMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uC218\uC815(\uC124\uC815)")}</button>
                   {(() => {
                        // [PATCH][ANCHOR:GAMELIST:GROUP_MISSION_START_LABEL_AND_COLOR]
                        // ✅ "실행중이면 재시작" UX
                        // ✅ 이 컴포넌트는 이미 위에서 run = ms.active || ms.active_run 으로 계산해둠
                        const isActive = !!run?.run_id;
                        return (<button type="button" onClick={() => startMissionRun(code)} disabled={groupMissionBusyCode === code} className={[
                                "px-3 py-1.5 rounded text-white disabled:opacity-60",
                                isActive
                                    ? "bg-amber-600 hover:bg-amber-500"
                                    : "bg-emerald-600 hover:bg-emerald-500",
                            ].join(" ")}>
                    {isActive ? t("\uC7AC\uC2DC\uC791") : t("\uC2DC\uC791")}
                  </button>);
                    })()}
                        {(() => {
                        // [FIX][ANCHOR:GAMELIST:GROUP_MISSION_STOP_RECOVERY_ENABLED]
                        const isActive = !!run?.run_id;
                        return (<button type="button" onClick={() => stopMissionRun(code)} disabled={groupMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60" title={isActive ? t("활성 그룹 미션 RUN 중지") : t("남아있는 그룹 미션 실행 상태 정리")}>{t("중지")}</button>);
                    })()}
                      </div>
                    </div>

                    {missionLoading[code] ? (<div className="mt-2 text-gray-600">{t("\uB85C\uB529\uC911...")}</div>) : (<>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <div className="text-gray-600">cfg.target_total_bet</div>
                            <div className="font-medium">{cfg ? fmtNumber(cfg.target_total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">active_run_id</div>
                            <div className="font-medium">{run?.run_id || '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.status</div>
                            <div className="font-medium">{run?.status || '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.total_bet</div>
                            <div className="font-medium">{run ? fmtNumber(run.total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.target_total_bet</div>
                            <div className="font-medium">{run ? fmtNumber(run.target_total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.created_at</div>
                            <div className="font-medium">
                              {run?.created_at ? new Date(Number(run.created_at)).toLocaleString() : '-'}
                            </div>
                          </div>
                        </div>

                        {/* 기존 상금표 렌더는 그대로 유지 */}
                        {(() => {
                            const baseTarget = (run?.target_total_bet ?? null) ||
                                (cfg?.target_total_bet ?? null) ||
                                null;
                            const agentShare = Number(cfg?.agent_share ?? 0.5);
                            const playerRate = 0.01;
                            const prize = calcMissionPrize(baseTarget, playerRate, agentShare);
                            if (!prize)
                                return null;
                            return (<div className="mt-4 p-3 rounded border bg-gray-50">
                              <div className="font-semibold mb-2">{t("\uBBF8\uC158 \uC0C1\uAE08 \uBD84\uBC30(\uD504\uB860\uD2B8 \uACC4\uC0B0)")}</div>
                              <div className="text-sm text-gray-700 mb-2">{t("\uCD1D\uBCA0\uD305\uC561:")}<span className="font-medium">{fmtNumber(baseTarget)}</span>
                                <span className="text-xs text-gray-500"> (agent_share: {prize.agent_share})</span>
                                <br />{t("\uD50C\uB808\uC774\uC5B4 \uC0C1\uAE08\uD480(")}{Math.round(prize.player_rate * 10000) / 100}%):{' '}
                                <span className="font-medium">{fmtNumber(prize.player_pool)}</span>
                                <br />{t("\uC5D0\uC774\uC804\uD2B8 \uC0C1\uAE08\uD480(")}{Math.round(prize.agent_rate * 10000) / 100}%):{' '}
                                <span className="font-medium">{fmtNumber(prize.agent_pool)}</span>
                                <br />{t("\uCD1D \uC0C1\uAE08\uD480(1.5%):")}<span className="font-medium">{fmtNumber(prize.total_pool)}</span>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="min-w-[520px] w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-600">
                                      <th className="py-1 pr-3">Rank</th>
                                      <th className="py-1 pr-3">Total</th>
                                      <th className="py-1 pr-3">Player</th>
                                      <th className="py-1 pr-3">Agent</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {prize.lines.map((p) => (<tr key={p.rank} className="border-t">
                                        <td className="py-1 pr-3 font-medium">{p.rank}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.total)}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.player)}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.agent)}</td>
                                      </tr>))}
                                    <tr className="border-t">
                                      <td className="py-1 pr-3 text-gray-600">player_remainder</td>
                                      <td className="py-1 pr-3">{fmtNumber(prize.player_remainder)}</td>
                                      <td className="py-1 pr-3"/>
                                      <td className="py-1 pr-3"/>
                                    </tr>
                                    <tr className="border-t">
                                      <td className="py-1 pr-3 text-gray-600">agent_remainder</td>
                                      <td className="py-1 pr-3">{fmtNumber(prize.agent_remainder)}</td>
                                      <td className="py-1 pr-3"/>
                                      <td className="py-1 pr-3"/>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>);
                        })()}
                      </>)}
                  </div>

                {/* ===== Table Mission Panel ===== */}
                  <div className="p-3 rounded border bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold">{t("\uD14C\uC774\uBE14 \uBBF8\uC158")}</div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button type="button" onClick={() => loadTableMissionStatus(code)} disabled={tableMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uC870\uD68C")}</button>
                        <button type="button" onClick={() => setTableMissionConfig(code)} disabled={tableMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uC218\uC815(\uC124\uC815)")}</button>
                        <button type="button" onClick={() => startTableMissionRun(code)} disabled={tableMissionBusyCode === code} className="px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60">{t("\uC2DC\uC791(\uC0DD\uC131)")}</button>
                        <button type="button" onClick={() => stopTableMissionRun(code)} disabled={tableMissionBusyCode === code} className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60">{t("\uC911\uC9C0(\uC0AD\uC81C)")}</button>
                      </div>
                    </div>

                    {tableMissionLoading[code] ? (<div className="mt-2 text-gray-600">{t("\uB85C\uB529\uC911...")}</div>) : (<>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <div className="text-gray-600">cfg.target_total_bet</div>
                            <div className="font-medium">{tcfg ? fmtNumber(tcfg.target_total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">cfg.payout_mode</div>
                            <div className="font-medium">{tcfg?.payout_mode || 'auto'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">active_run_id</div>
                            <div className="font-medium">{trun?.run_id || '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.status</div>
                            <div className="font-medium">{trun?.status || '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.total_bet</div>
                            <div className="font-medium">{trun ? fmtNumber(trun.total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.target_total_bet</div>
                            <div className="font-medium">{trun ? fmtNumber(trun.target_total_bet) : '-'}</div>
                          </div>
                          <div>
                            <div className="text-gray-600">run.created_at</div>
                            <div className="font-medium">
                              {trun?.created_at ? new Date(Number(trun.created_at)).toLocaleString() : '-'}
                            </div>
                          </div>
                        </div>

                        {/* 테이블 미션 상금표(테이블 미션 기준) */}
                        {(() => {
                            const baseTarget = (trun?.target_total_bet ?? null) ||
                                (tcfg?.target_total_bet ?? null) ||
                                null;
                            const agentShare = Number(tcfg?.agent_share ?? 0.5);
                            const playerRate = 0.01;
                            // 최신 운영 설정(cfg)을 우선 사용한다.
                            // 활성 RUN이 과거 auto 상태로 남아 있어도 수정(설정) 직후 manual 값이 즉시 표시되어야 한다.
                            const payoutMode = tcfg?.payout_mode || trun?.payout_mode || 'auto';
                            const manualPayouts = (Array.isArray(tcfg?.manual_payouts) && tcfg.manual_payouts.length)
                                ? tcfg.manual_payouts
                                : (Array.isArray(trun?.manual_payouts) ? trun.manual_payouts : []);
                            const prize = calcMissionPrize(baseTarget, playerRate, agentShare, payoutMode, manualPayouts);
                            if (!prize)
                                return null;
                            return (<div className="mt-4 p-3 rounded border bg-gray-50">
                              <div className="font-semibold mb-2">{t("\uD14C\uC774\uBE14 \uBBF8\uC158 \uC0C1\uAE08 \uBD84\uBC30(\uD504\uB860\uD2B8 \uACC4\uC0B0)")}</div>
                              <div className="text-sm text-gray-700 mb-2">{t("\uCD1D\uBCA0\uD305\uC561:")}<span className="font-medium">{fmtNumber(baseTarget)}</span>
                                <span className="text-xs text-gray-500"> ({prize.payout_mode === 'manual' ? t("수동 설정") : `agent_share: ${prize.agent_share}`})</span>
                                {prize.payout_mode === 'manual' ? (<>
                                  <br />{t("플레이어 수동 상금 합계:")}{' '}
                                  <span className="font-medium">{fmtNumber(prize.player_pool)}</span>
                                  <br />{t("에이전트 수동 상금 합계:")}{' '}
                                  <span className="font-medium">{fmtNumber(prize.agent_pool)}</span>
                                  <br />{t("총 수동 상금:")}{' '}
                                  <span className="font-medium">{fmtNumber(prize.total_pool)}</span>
                                </>) : (<>
                                  <br />{t("\uD50C\uB808\uC774\uC5B4 \uC0C1\uAE08\uD480(")}{Math.round(prize.player_rate * 10000) / 100}%):{' '}
                                  <span className="font-medium">{fmtNumber(prize.player_pool)}</span>
                                  <br />{t("\uC5D0\uC774\uC804\uD2B8 \uC0C1\uAE08\uD480(")}{Math.round(prize.agent_rate * 10000) / 100}%):{' '}
                                  <span className="font-medium">{fmtNumber(prize.agent_pool)}</span>
                                  <br />{t("\uCD1D \uC0C1\uAE08\uD480(1.5%):")}{' '}
                                  <span className="font-medium">{fmtNumber(prize.total_pool)}</span>
                                </>)}
                              </div>
                              <div className="overflow-x-auto">
                                <table className="min-w-[520px] w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-600">
                                      <th className="py-1 pr-3">Rank</th>
                                      <th className="py-1 pr-3">Total</th>
                                      <th className="py-1 pr-3">Player</th>
                                      <th className="py-1 pr-3">Agent</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {prize.lines.map((p) => (<tr key={p.rank} className="border-t">
                                        <td className="py-1 pr-3 font-medium">{p.rank}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.total)}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.player)}</td>
                                        <td className="py-1 pr-3">{fmtNumber(p.agent)}</td>
                                      </tr>))}
                                    <tr className="border-t">
                                      <td className="py-1 pr-3 text-gray-600">player_remainder</td>
                                      <td className="py-1 pr-3">{fmtNumber(prize.player_remainder)}</td>
                                      <td className="py-1 pr-3"/>
                                      <td className="py-1 pr-3"/>
                                    </tr>
                                    <tr className="border-t">
                                      <td className="py-1 pr-3 text-gray-600">agent_remainder</td>
                                      <td className="py-1 pr-3">{fmtNumber(prize.agent_remainder)}</td>
                                      <td className="py-1 pr-3"/>
                                      <td className="py-1 pr-3"/>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>);
                        })()}

                        <details className="mt-3">
                          <summary className="cursor-pointer text-sm text-gray-700">{t("\uC0C1\uD0DC \uBCF4\uAE30 (\uD14C\uC774\uBE14 \uBBF8\uC158 raw)")}</summary>
                          <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-72">
                            {prettyJson(tableMissionByCode?.[code] || null)}
                          </pre>
                        </details>
                      </>)}
                  </div>
                </div>
              </div>) : null}
          </div>);
        })}

      {/* [ADD][ANCHOR:INFO_EDIT:MODAL] */}
      {infoEdit ? (<div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 overflow-y-auto p-4">
          <div className="w-full max-w-[1680px] rounded bg-white shadow-lg overflow-auto max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">
                {infoEdit.mode === 'create' ? t("\uC778\uD3EC\uCE74\uB4DC \uCD94\uAC00") : t("\uC778\uD3EC\uBC84\uD2BC \uD504\uB9AC\uC14B \uC218\uC815")} <span className="text-xs text-gray-500">({infoEdit.code})</span>
              </div>
              <button type="button" className="px-2 py-1 rounded border hover:bg-gray-100" onClick={closeInfoDialog} disabled={infoSaving}>{t("\uB2EB\uAE30")}</button>
            </div>

            <div className="p-4">
              {renderInfoTargetVariantMatrix()}
              <InfoPresetBuilder title={infoEdit.mode === 'create' ? 'New Information Button' : 'Information Button'} value={infoDraft} onChange={setInfoDraft} enableAdvancedDesign/>
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-gray-50">
              <div>
                {infoEdit.mode === 'edit' ? (<button type="button" className="px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60" onClick={() => deleteInfoCard(infoEdit.code, infoEdit.row)} disabled={infoSaving}>{t("\uC778\uD3EC\uCE74\uB4DC \uC0AD\uC81C")}</button>) : null}
              </div>
              <div className="flex justify-end gap-2">
              <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-100" onClick={closeInfoDialog} disabled={infoSaving}>{t("\uCDE8\uC18C")}</button>
              <button type="button" className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60" onClick={saveInfoEdit} disabled={infoSaving}>
                {infoEdit.mode === 'create' ? t("\uCD94\uAC00") : t("\uC800\uC7A5")}
              </button>
              </div>
            </div>
          </div>
        </div>) : null}
    </div>);
}
